import '@tamagui/core/reset.css'

import { useNavigate, useParams } from 'react-router-dom'
import { Header } from '../../components/Header'
import { createOrganization, CreateOrganizationData, getMyOrganizations, getOrganization, MyOrganizationsData, OrganizationData } from '../../remote/organizations'
import { useCallback, useEffect, useState } from 'react'
import { ManageSuppliers } from '../../components/ManageSuppliers'
import { ManageDashboard } from '../../components/ManageDashboard'
import { Box, Divider, Tabs, Tab, Stack, Typography, CircularProgress, Paper } from '@mui/material'
import { Button, Form, Input, Label, TextArea, XStack, YStack } from 'tamagui'
import { Check } from '@mui/icons-material'
import { ManageQuestionnaires } from '../../components/ManageQuestionnaires'
import { OrganizationContext } from '../../contexts/OrganizationContext'

export type ManageOrganizationTab = 'tab_dashboard' | 'tab_suppliers' | 'tab_members' | 'tab_org' | undefined;

export class ManageOrganizationProps {
	tab: ManageOrganizationTab

	constructor(tab: ManageOrganizationTab) {
		this.tab = tab;
	}
}

export const ManageOrganization = (props: ManageOrganizationProps) => {
	const navigate = useNavigate()
	const params = useParams()
	const orgId = params.orgId;

	const [orgData, setOrgData] = useState<OrganizationData | null>(null);
	const [isLoaded, setIsLoaded] = useState(false)
	useEffect(() => {
		if (orgId) {
			getOrganization(orgId, (data, resp) => {
				if (data) {
					setOrgData(data);
					setIsLoaded(true);
				} else {
					setOrgData(null);
					setIsLoaded(false);
				}
			});
		}
	}, [setOrgData, setIsLoaded]);

	const [tabValue, setTabValue] = useState(props.tab == undefined ? 0 : {
		'tab_dashboard': 0,
		'tab_suppliers': 1,
		'tab_questionnaires': 2,
		'tab_members': 3,
		'tab_org': 4,
	}[props.tab]);

	const navigateTab = useCallback((tab, orgId) => {
		setTabValue(tab)
		const path = {
			0: 'dashboard',
			1: 'suppliers',
			2: `questionnaires`,
			3: 'members',
			4: `${orgId}/edit`,
		}[tab];
		if (props.tab != tab) {
			navigate(props.tab == undefined ? '' : '../' + path, { relative: "path" })
		}
	}, [navigate, props, setTabValue])

	if (orgId == null) {
		return <Typography variant="h2">Invalid organization, try logging out and back in?</Typography>
	}

	return (
	  <OrganizationContext value={orgId}>
		<Header/>
		<Stack direction='row'>
		  {
			isLoaded && orgData
			? <>
				<Box sx={{m: '2em', width: '100%'}}>
					<Stack direction='column'>
					<Typography variant="h3">{orgData.name}</Typography>
					<Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '1em' }}>
						<Tabs value={tabValue} onChange={(_, tab) => navigateTab(tab, orgData.id)}>
							<Tab label="Dashboard" id="tab_dashboard" aria-controls={`simple-tabpanel-0`} />
							<Tab label="Suppliers" id="tab_suppliers" aria-controls={`simple-tabpanel-1`} />
							<Tab label="Questionnaires" id="tab_questionnaires" aria-controls={`simple-tabpanel-4`} />
							<Tab label="Members" id="tab_members" aria-controls={`simple-tabpanel-2`} />
							<Tab label="Edit Organization" id="tab_org" aria-controls={`simple-tabpanel-3`} />
						</Tabs>
					</Box>
					<div role="tabpanel" hidden={tabValue !== 0} id={`tab_dashboard`} aria-labelledby={`simple-tab-0`}>
						<ManageDashboard org={orgData.id} />
					</div>
					<div role="tabpanel" hidden={tabValue !== 1} id={`tab_suppliers`} aria-labelledby={`simple-tab-1`}>
						<ManageSuppliers org={orgData.id} />
					</div>
					<div role="tabpanel" hidden={tabValue !== 2} id={`tab_questionnaires`} aria-labelledby={`simple-tab-4`}>
						<ManageQuestionnaires org={orgData.id} />
					</div>
					<div role="tabpanel" hidden={tabValue !== 3} id={`tab_members`} aria-labelledby={`simple-tab-2`}>
					</div>
					<div role="tabpanel" hidden={tabValue !== 4} id={`tab_org`} aria-labelledby={`simple-tab-3`}>
						<EditOrganizationForm org={orgData.id} />
					</div>
				</Stack>
				</Box>
			</>
			: <>
				<CircularProgress />
			</>
		  }
		</Stack>
	  </OrganizationContext>
	)
}

class EditOrganizationFormProps {
	org: string

	constructor(org: string) {
		this.org = org
	}
}

export const EditOrganizationForm = (props: EditOrganizationFormProps) => {
	const [status, setStatus] = useState<'off' | 'submitting' | 'submitted'>('off')
	useEffect(() => {
		if (status === 'submitting') {
			const timer = setTimeout(() => setStatus('off'), 20000)
			return () => {
				clearTimeout(timer)
			}
		}
	}, [status])

	const [name, setName] = useState('')
	const handleNameInputChange = (event) => {
		setName(event.target.value)
	}

	const [email, setEmail] = useState('')
	const handleEmailInputChange = (event) => {
		setEmail(event.target.value)
	}

	const [phone, setPhone] = useState('')
	const handlePhoneInputChange = (event) => {
		setPhone(event.target.value)
	}

	const [description, setDescription] = useState('')
	const handleDescriptionInputChange = (event) => {
		setDescription(event.target.value)
	}

	const [orgData, setOrgData] = useState<OrganizationData | null>(null);
	const [isLoaded, setIsLoaded] = useState(false)
	useEffect(() => {
		if (props.org) {
			getOrganization(props.org, (data, resp) => {
				if (data) {
					setOrgData(data);
					setIsLoaded(true);
					setName(data.name);
					setEmail(data.email);
					setPhone(data.phone);
				} else {
					setOrgData(null);
					setIsLoaded(false);
				}
			});
		}
	}, [setOrgData, setIsLoaded]);

	const submitForm = () => {
		setStatus('submitting')
		createOrganization(new CreateOrganizationData(name, email, phone, description), (data, response) => {
			if (response?.status == 200) {
				setStatus('submitted')
			}
		});
	}

	return (
		<>
				{isLoaded && orgData ? <Form
					alignItems="center"
					minWidth={600}
					gap="$2"
					onSubmit={submitForm}
					borderWidth={1}
					borderRadius="$4"
					backgroundColor="$background"
					borderColor="$borderColor"
					padding="$8"
				>
					<XStack alignItems="center" gap="$4">
						<Label htmlFor='name' width={120}>Name</Label>
						<Input id='name' value={name} onChange={handleNameInputChange} size="$4" borderWidth={2} flex={1} />
					</XStack>
					<XStack alignItems="center" gap="$4">
						<Label htmlFor='email' width={120}>Email address</Label>
						<Input id='email' value={email} onChange={handleEmailInputChange} inputMode='email' size="$4" borderWidth={2} flex={1} />
					</XStack>
					<XStack alignItems="center" gap="$4">
						<Label htmlFor='phone' width={120}>Phone number</Label>
						<Input id='phone' value={phone} onChange={handlePhoneInputChange} inputMode='tel' size="$4" borderWidth={2} flex={1} />
					</XStack>
					<XStack alignItems="center" gap="$4">
						<Label htmlFor='description' width={120}>Description</Label>
						<TextArea id='description' value={description} onChange={handleDescriptionInputChange} size="$4" borderWidth={2} flex={1} />
					</XStack>
					<Form.Trigger asChild>
						<Button icon={status === 'submitting' ? () => <CircularProgress /> : status === 'submitted' ? Check : null}>Save</Button>
					</Form.Trigger>
				</Form>
				: <CircularProgress></CircularProgress>}
		</>
	)
}

export const CreateOrganization = () => {
	const navigate = useNavigate()

	const [status, setStatus] = useState<'off' | 'submitting' | 'submitted'>('off')
	useEffect(() => {
		if (status === 'submitting') {
			const timer = setTimeout(() => setStatus('off'), 20000)
			return () => {
				clearTimeout(timer)
			}
		}
	}, [status])

	const [name, setName] = useState('')
	const handleNameInputChange = (event) => {
		setName(event.target.value)
	}

	const [email, setEmail] = useState('')
	const handleEmailInputChange = (event) => {
		setEmail(event.target.value)
	}

	const [phone, setPhone] = useState('')
	const handlePhoneInputChange = (event) => {
		setPhone(event.target.value)
	}

	const [description, setDescription] = useState('')
	const handleDescriptionInputChange = (event) => {
		setDescription(event.target.value)
	}

	const submitForm = () => {
		setStatus('submitting')
		createOrganization(new CreateOrganizationData(name, email, phone, description), (data, response) => {
			if (response?.status == 200) {
				navigate('/manage/')
				setStatus('submitted')
			}
		});
	}

	return (
		<>
			<Header />
			<YStack flex={1} items="center" justify="center">
				<Form
					alignItems="center"
					minWidth={600}
					gap="$2"
					onSubmit={submitForm}
					borderWidth={1}
					borderRadius="$4"
					backgroundColor="$background"
					borderColor="$borderColor"
					padding="$8"
				>
					<Typography variant="h4">Create an organization</Typography>
					<XStack alignItems="center" gap="$4">
						<Label htmlFor='name' width={120}>Name</Label>
						<Input id='name' value={name} onChange={handleNameInputChange} size="$4" borderWidth={2} flex={1} />
					</XStack>
					<XStack alignItems="center" gap="$4">
						<Label htmlFor='email' width={120}>Email address</Label>
						<Input id='email' value={email} onChange={handleEmailInputChange} inputMode='email' size="$4" borderWidth={2} flex={1} />
					</XStack>
					<XStack alignItems="center" gap="$4">
						<Label htmlFor='phone' width={120}>Phone number</Label>
						<Input id='phone' value={phone} onChange={handlePhoneInputChange} inputMode='tel' size="$4" borderWidth={2} flex={1} />
					</XStack>
					<XStack alignItems="center" gap="$4">
						<Label htmlFor='description' width={120}>Description</Label>
						<TextArea id='description' value={description} onChange={handleDescriptionInputChange} size="$4" borderWidth={2} flex={1} />
					</XStack>
					<Form.Trigger asChild>
						<Button icon={status === 'submitting' ? () => <CircularProgress /> : status === 'submitted' ? Check : null}>Register</Button>
					</Form.Trigger>
				</Form>
			</YStack>
		</>
	)
}
